import {
  Box,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Menu,
} from "@mui/material";
import { useState, useMemo, useContext } from "react";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";

import {
  useCustomer,
  useRefetchCustomerSettings,
} from "../../../../contexts/app/provider";
import { isDatePivotParamsEqual } from "../../../../utils/is_date_pivot_params_equal";
import { DatePivotParams } from "../../../../entities/date_pivot_params";
import { useApi } from "../../../../contexts/app/provider";
import { useStoreMyViewForPreloadMutation } from "../../../../services/api/__generated__/backend_gateway-types";
import { GetMyCustomerDocument } from "../../../../services/api/__generated__/backend_gateway-types";
import { SysMessageLevel } from "../../../../contexts/sys_messages/type";
import { SysMessagesContext } from "../../../../contexts/sys_messages/context";
import { CreatePinDialog } from "./create_pin_dialog";
import { DeletePinDialog } from "./delete_pin_dialog";
import { RenamePinDialog } from "./rename_pin_dialog";
import { UpdatePinDialog } from "./update_pin_dialog";

type PinnedView = {
  id: string;
  name: string;
  paramsJSON: string;
  isDefault?: boolean;
};

export const PinnedViewsSelector = ({
  isLoading,
  datasetName,
  transformId,
  params,
  setParams,
}: {
  isLoading: boolean;
  datasetName: string;
  transformId: string;
  params: DatePivotParams;
  setParams: (params: DatePivotParams) => void;
}) => {
  const { apiService } = useApi();
  const { customerSettings } = useCustomer();
  const { refetchCustomerSettings } = useRefetchCustomerSettings();
  const { addSystemMessage } = useContext(SysMessagesContext);
  const [storeMyViewForPreloadMutation] = useStoreMyViewForPreloadMutation({
    client: apiService.getClient(),
    // Update the cache directly to ensure UI updates immediately
    refetchQueries: ["GetMyCustomer"],
    awaitRefetchQueries: true,
    update: (cache, { data }) => {
      // Directly update the cache with the mutation response
      if (data?.storeMyViewForPreload) {
        cache.writeQuery({
          query: GetMyCustomerDocument,
          data: {
            getMyCustomer: data.storeMyViewForPreload,
          },
        });
      }
    },
  });

  const [pinMenuAnchorEl, setPinMenuAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [createPinDialogOpen, setCreatePinDialogOpen] = useState(false);
  const [renamePinDialogOpen, setRenamePinDialogOpen] = useState(false);
  const [deletePinDialogOpen, setDeletePinDialogOpen] = useState(false);
  const [pinToDelete, setPinToDelete] = useState<PinnedView | null>(null);
  const [updatePinDialogOpen, setUpdatePinDialogOpen] = useState(false);

  const viewKey = `${datasetName}|${transformId}`;

  // Extract all pins for this view from preloadDatasets
  const pinnedViews = useMemo((): PinnedView[] => {
    const preloadDatasets = customerSettings?.preloadDatasets || [];
    const viewPin = preloadDatasets.find(
      (p) => p.viewDatasetTransform === viewKey
    );

    if (!viewPin) {
      return [];
    }

    try {
      const parsed = JSON.parse(viewPin.paramsJSON);

      // Handle array of configs (pins with names)
      if (Array.isArray(parsed)) {
        const pins = parsed
          .filter((config) => config && typeof config === "object")
          .map((config, index) => ({
            id: config.name || `pin-${index}`,
            name: config.name || "unnamed pin",
            paramsJSON: JSON.stringify(config),
            isDefault: config.isDefault === true,
          }));

        // Remove duplicates by name (case-insensitive) - keep the first occurrence
        const seenNames = new Set<string>();
        const uniquePins = pins.filter((pin) => {
          const normalizedName = pin.name.toLowerCase().trim();
          if (seenNames.has(normalizedName)) {
            return false;
          }
          seenNames.add(normalizedName);
          return true;
        });

        // Sort alphabetically by name (case-insensitive)
        return uniquePins.sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );
      }

      // Handle single config object (backward compatibility)
      // If it has a name, use it; otherwise treat as "Default"
      if (parsed && typeof parsed === "object") {
        return [
          {
            id: parsed.name || "default",
            name: parsed.name || "Default",
            paramsJSON: viewPin.paramsJSON,
            isDefault: parsed.isDefault === true || !parsed.name,
          },
        ];
      }

      return [];
    } catch (error) {
      console.error("Error parsing pinned views", error);
      return [];
    }
  }, [customerSettings, viewKey]);

  // Find the currently active pin (if any matches current params)
  const activePin = useMemo((): PinnedView | null => {
    return (
      pinnedViews.find((pin) => {
        const storedParams = JSON.parse(pin.paramsJSON);
        return isDatePivotParamsEqual(params, storedParams);
      }) || null
    );
  }, [pinnedViews, params]);

  // Check if current params match any pinned view
  const isPinned = activePin !== null;

  const handlePinMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPinMenuAnchorEl(event.currentTarget);
  };

  const handlePinIconClick = (event: React.MouseEvent<HTMLElement>) => {
    if (pinnedViews.length === 0) {
      // If there are no existing pins, open create pin dialog directly (current behavior)
      setCreatePinDialogOpen(true);
      return;
    }

    // Otherwise, always open the pin actions menu
    handlePinMenuOpen(event);
  };

  const handlePinMenuClose = () => {
    setPinMenuAnchorEl(null);
  };

  const handleSelectPin = (pin: PinnedView) => {
    try {
      const storedParams = JSON.parse(pin.paramsJSON);

      // Convert date strings to Date objects if needed
      const processedParams = { ...storedParams };
      if (
        processedParams.minDate &&
        typeof processedParams.minDate === "string"
      ) {
        processedParams.minDate = new Date(processedParams.minDate);
      }
      if (
        processedParams.maxDate &&
        typeof processedParams.maxDate === "string"
      ) {
        processedParams.maxDate = new Date(processedParams.maxDate);
      }

      // Remove name and isDefault from params before setting (these are metadata, not params)
      const { name, isDefault, ...paramsToSet } = processedParams;

      // Apply the selected pin's params to the view
      setParams(paramsToSet);
      addSystemMessage({
        level: SysMessageLevel.SUCCESS,
        message: `View settings loaded from "${pin.name}".`,
      });
      handlePinMenuClose();
    } catch (error) {
      console.error("Error parsing pin params", error);
      addSystemMessage({
        level: SysMessageLevel.DANGER,
        message: "Failed to load pinned view.",
      });
    }
  };

  const handleCreateNewPin = (pinName: string, setAsDefault: boolean) => {
    setCreatePinDialogOpen(false);
    storeMyViewForPreload({
      pinName,
      setAsDefault,
    });
  };

  const storeMyViewForPreload = async (options?: {
    pinName?: string;
    setAsDefault?: boolean;
    oldPinName?: string;
    deletePinName?: string;
    paramsToStore?: DatePivotParams;
    forceReload?: boolean;
  }) => {
    // For rename, use the provided params; for delete, use empty; otherwise use current params
    const paramsToUse = options?.paramsToStore || params;
    const paramsJSON = options?.deletePinName
      ? "{}"
      : JSON.stringify(paramsToUse);

    await storeMyViewForPreloadMutation({
      variables: {
        datasetName,
        transformId,
        paramsJSON,
        pinName: options?.pinName || null,
        setAsDefault: options?.setAsDefault || null,
        oldPinName: options?.oldPinName || null,
        deletePinName: options?.deletePinName || null,
      },
      onCompleted: async (data) => {
        // The refetchQueries option will automatically refetch GetMyCustomer
        // But we also manually refetch to ensure the context updates immediately
        try {
          await refetchCustomerSettings();
          // Small delay to ensure the UI has time to update
          await new Promise((resolve) => setTimeout(resolve, 100));
        } catch (error) {
          console.error("Error refetching customer settings", error);
        }

        // Show success message
        if (options?.deletePinName) {
          addSystemMessage({
            level: SysMessageLevel.SUCCESS,
            message: `Pin "${options.deletePinName}" deleted successfully.`,
          });
        } else if (
          options?.oldPinName &&
          options?.pinName &&
          options.oldPinName !== options.pinName
        ) {
          // Only show rename message if names are actually different
          addSystemMessage({
            level: SysMessageLevel.SUCCESS,
            message: `Pin renamed from "${options.oldPinName}" to "${options.pinName}".`,
          });
        } else if (options?.pinName) {
          // Check if we're setting as default
          if (options.setAsDefault) {
            addSystemMessage({
              level: SysMessageLevel.SUCCESS,
              message: `Pin "${options.pinName}" set as default.`,
            });
          } else {
            addSystemMessage({
              level: SysMessageLevel.SUCCESS,
              message: `View settings saved as "${options.pinName}".`,
            });
          }
        } else {
          addSystemMessage({
            level: SysMessageLevel.SUCCESS,
            message: "View settings stored as default.",
          });
        }

        if (options?.forceReload) {
          window.location.reload();
        }

        // No need to refresh - the hook now prevents unnecessary reloads
        // by comparing the selected config rather than the entire paramsJSON
      },
      onError: (error) => {
        console.error("storeMyViewForPreload error", error);
        const errorMessage = error.message || "Failed to store view settings.";
        addSystemMessage({
          level: SysMessageLevel.DANGER,
          message: errorMessage,
        });
      },
    });
  };

  const handleDeletePin = (pin: PinnedView) => {
    setPinToDelete(pin);
    setDeletePinDialogOpen(true);
    handlePinMenuClose();
  };

  const confirmDeletePin = async () => {
    if (!pinToDelete) return;

    await storeMyViewForPreload({
      deletePinName: pinToDelete.name,
    });

    setDeletePinDialogOpen(false);
    setPinToDelete(null);
  };

  const handleStoreAsDefault = async () => {
    if (activePin) {
      // Update the existing pin to set it as default
      // Find the pin's current params to preserve them
      const pinToUpdate = pinnedViews.find((p) => p.id === activePin.id);
      if (!pinToUpdate) return;

      try {
        const currentParams = JSON.parse(pinToUpdate.paramsJSON);
        // Remove metadata fields before storing
        const { name, isDefault, ...paramsToStore } = currentParams;

        // Check if the pin actually has a name property in the stored config
        // A pin without a name property (or with empty/undefined name) is truly unnamed
        const hasNameProperty = "name" in currentParams && currentParams.name;
        const storedName = hasNameProperty
          ? String(currentParams.name).trim()
          : "";

        // For pins without a real name property, pass empty string as oldPinName
        // so backend can find the config without a name property
        const oldPinNameForUpdate = hasNameProperty ? storedName : "";

        // When setting as default, we keep the same name
        // If it's an unnamed pin, pass empty string so backend knows to keep it unnamed
        const pinNameForUpdate = hasNameProperty ? storedName : "";

        await storeMyViewForPreload({
          pinName: pinNameForUpdate, // Empty string for unnamed pins, otherwise the actual name
          oldPinName: oldPinNameForUpdate, // Pass oldPinName to indicate we're updating, not creating
          setAsDefault: true,
          paramsToStore: paramsToStore as DatePivotParams,
        });
      } catch (error) {
        console.error("Error setting pin as default", error);
        addSystemMessage({
          level: SysMessageLevel.DANGER,
          message: "Failed to set pin as default.",
        });
      }
    } else {
      // If no active pin, just store as default (backward compatibility)
      await storeMyViewForPreload();
    }
  };

  const handleRenamePin = async (newPinName: string) => {
    if (!activePin) return;

    setRenamePinDialogOpen(false);

    // Find the pin's current params to preserve them
    const pinToRename = pinnedViews.find((p) => p.id === activePin.id);
    if (!pinToRename) return;

    try {
      const currentParams = JSON.parse(pinToRename.paramsJSON);
      // Remove metadata fields before storing
      const { name, isDefault, ...paramsToStore } = currentParams;

      // Check if the pin actually has a name property in the stored config
      // A pin without a name property (or with empty/undefined name) is truly unnamed
      const hasNameProperty = "name" in currentParams && currentParams.name;
      const storedName = hasNameProperty
        ? String(currentParams.name).trim()
        : "";

      // For pins without a real name property, pass empty string as oldPinName
      // so backend can find the config without a name property
      const oldPinNameForRename = hasNameProperty ? storedName : "";

      await storeMyViewForPreload({
        pinName: newPinName,
        oldPinName: oldPinNameForRename,
        setAsDefault: activePin.isDefault || false,
        paramsToStore: paramsToStore as DatePivotParams,
      });

      // The params should remain the same, just the name changes
      // The refetch will update the UI with the new name
    } catch (error) {
      console.error("Error renaming pin", error);
      addSystemMessage({
        level: SysMessageLevel.DANGER,
        message: "Failed to rename pin.",
      });
    }
  };

  const handleUnpin = async () => {
    if (!activePin) return;

    // Find the pin's current params to check if it has a name property
    const pinToDelete = pinnedViews.find((p) => p.id === activePin.id);
    if (!pinToDelete) return;

    try {
      const currentParams = JSON.parse(pinToDelete.paramsJSON);

      // Check if the pin actually has a name property in the stored config
      // A pin without a name property (or with empty/undefined name) is truly unnamed
      const hasNameProperty = "name" in currentParams && currentParams.name;
      const storedName = hasNameProperty
        ? String(currentParams.name).trim()
        : "";

      // For pins without a real name property, pass empty string as deletePinName
      // so backend can find and delete the config without a name property
      const pinNameToDelete = hasNameProperty ? storedName : "";

      await storeMyViewForPreload({
        deletePinName: pinNameToDelete,
      });

      handlePinMenuClose();
    } catch (error) {
      console.error("Error unpinning", error);
      addSystemMessage({
        level: SysMessageLevel.DANGER,
        message: "Failed to unpin.",
      });
    }
  };

  const handleUpdateExistingPin = async (
    pinId: string,
    setAsDefault: boolean,
    newName?: string
  ) => {
    const pinToUpdate = pinnedViews.find((p) => p.id === pinId);
    if (!pinToUpdate) return;

    try {
      const currentParams = JSON.parse(pinToUpdate.paramsJSON);

      // Check if the pin actually has a name property in the stored config
      // A pin without a name property (or with empty/undefined name) is truly unnamed
      const hasNameProperty = "name" in currentParams && currentParams.name;
      const storedName = hasNameProperty
        ? String(currentParams.name).trim()
        : "";

      // For pins without a real name property, pass empty string as oldPinName
      // so backend can find the config without a name property
      const oldPinNameForUpdate = hasNameProperty ? storedName : "";

      // When updating, we keep the same name (or keep it unnamed) unless a new
      // name was provided from the update dialog.
      const pinNameForUpdate =
        typeof newName === "string" && newName.trim()
          ? newName.trim()
          : hasNameProperty
            ? storedName
            : "";

      await storeMyViewForPreload({
        pinName: pinNameForUpdate,
        oldPinName: oldPinNameForUpdate,
        setAsDefault,
        paramsToStore: params,
        forceReload: true,
      });

      setUpdatePinDialogOpen(false);
    } catch (error) {
      console.error("Error updating pin", error);
      addSystemMessage({
        level: SysMessageLevel.DANGER,
        message: "Failed to update pin.",
      });
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {/* Dropdown to select active pin */}
      <Select
        disabled={isLoading || pinnedViews.length === 0}
        variant="standard"
        size="small"
        value={activePin?.id ? String(activePin.id) : ""}
        displayEmpty
        renderValue={(value) => {
          const pin = value
            ? pinnedViews.find((p) => p.id === value)
            : null;
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                whiteSpace: "nowrap",
              }}
            >
              {pin?.isDefault ? (
                <StarIcon sx={{ fontSize: 15, color: "#F5A623" }} />
              ) : (
                <StarOutlineIcon
                  sx={{ fontSize: 15, color: (t) => t.palette.atomic.muted }}
                />
              )}
              <Box
                sx={{
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: (t) =>
                    pin ? t.palette.atomic.ink : t.palette.atomic.inkSecondary,
                }}
              >
                {pin ? pin.name || "Unknown" : "Not pinned"}
              </Box>
            </Box>
          );
        }}
        onChange={(event) => {
          const pinId = String(event.target.value);
          const pin = pinnedViews.find((p) => String(p.id) === pinId);
          if (pin) {
            handleSelectPin(pin);
          }
        }}
        sx={{
          height: 34,
          backgroundColor: "#fff",
          border: (t) => `1px solid ${t.palette.atomic.border}`,
          borderRadius: "6px",
          minWidth: "140px",
          "&::before": { borderBottom: "none !important" },
          "&::after": { borderBottom: "none !important" },
          "&:hover:not(.Mui-disabled)::before": {
            borderBottom: "none !important",
          },
          "& .MuiSelect-select": {
            padding: "0 28px 0 10px !important",
            display: "flex",
            alignItems: "center",
            minHeight: "unset !important",
          },
          "& .MuiSelect-icon": { color: (t) => t.palette.atomic.muted },
        }}
      >
        {pinnedViews.map((pin) => (
          <MenuItem key={pin.id} value={pin.id}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box>{pin.name}</Box>
              {pin.isDefault && (
                <StarIcon sx={{ fontSize: 14, color: "#F5A623" }} />
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>

      {/* Pin icon button with menu */}
      <Tooltip
        title={
          isPinned
            ? `Currently pinned as "${activePin?.name}"`
            : "Pin view settings"
        }
        sx={{ mt: 1 }}
      >
        <IconButton
          disabled={isLoading}
          onClick={handlePinIconClick}
          sx={{ mt: 1 }}
        >
          {isPinned ? (
            <PushPinIcon sx={{ height: 25 }} />
          ) : (
            <PushPinOutlinedIcon sx={{ height: 25 }} />
          )}
        </IconButton>
      </Tooltip>

      {/* Pin menu */}
      <Menu
        anchorEl={pinMenuAnchorEl}
        open={Boolean(pinMenuAnchorEl)}
        onClose={handlePinMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {pinnedViews.map((pin) => (
          <MenuItem
            key={pin.id}
            onClick={() => handleSelectPin(pin)}
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: "200px",
              backgroundColor:
                activePin?.id === pin.id ? "action.selected" : "transparent",
              "&:hover": {
                backgroundColor:
                  activePin?.id === pin.id ? "action.selected" : "action.hover",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box>{pin.name}</Box>
              {pin.isDefault && (
                <StarIcon sx={{ fontSize: 14, color: "#F5A623" }} />
              )}
            </Box>
          </MenuItem>
        ))}
        {/* Array, not a Fragment: MUI Menu forwards focus/keyboard props to
            each child, which a Fragment can't receive (console warning). */}
        {!isPinned && pinnedViews.length > 0 && [
          <MenuItem
            key="update-existing-pin"
            onClick={() => {
              setUpdatePinDialogOpen(true);
              handlePinMenuClose();
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderTop: "1px solid",
              borderColor: "divider",
              mt: 0.5,
            }}
          >
            <Box>Update existing pin</Box>
          </MenuItem>,
          <MenuItem
            key="create-new-pin"
            onClick={() => {
              setCreatePinDialogOpen(true);
              handlePinMenuClose();
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box>Create new pin</Box>
          </MenuItem>,
        ]}
        {isPinned && activePin && !activePin.isDefault && (
          <MenuItem
            onClick={() => {
              handleStoreAsDefault();
              handlePinMenuClose();
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderTop: "1px solid",
              borderColor: "divider",
              mt: 0.5,
            }}
          >
            <Box>Set as default</Box>
          </MenuItem>
        )}
        {isPinned && activePin && (
          <MenuItem
            onClick={() => {
              setRenamePinDialogOpen(true);
              handlePinMenuClose();
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderTop: !activePin.isDefault ? "none" : "1px solid",
              borderColor: "divider",
              mt: !activePin.isDefault ? 0 : 0.5,
            }}
          >
            <Box>Rename</Box>
          </MenuItem>
        )}
        {isPinned && activePin && (
          <MenuItem
            onClick={() => {
              handleUnpin();
              handlePinMenuClose();
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box>Unpin</Box>
          </MenuItem>
        )}
      </Menu>

      {/* Create pin dialog */}
      <CreatePinDialog
        open={createPinDialogOpen}
        onClose={() => setCreatePinDialogOpen(false)}
        onConfirm={handleCreateNewPin}
        existingPinNames={pinnedViews.map((p) => p.name)}
      />

      {/* Update pin dialog */}
      <UpdatePinDialog
        open={updatePinDialogOpen}
        onClose={() => setUpdatePinDialogOpen(false)}
        onConfirm={handleUpdateExistingPin}
        pins={pinnedViews}
      />

      {/* Rename pin dialog */}
      <RenamePinDialog
        open={renamePinDialogOpen}
        onClose={() => setRenamePinDialogOpen(false)}
        onConfirm={handleRenamePin}
        currentPinName={activePin?.name || ""}
        existingPinNames={pinnedViews.map((p) => p.name)}
      />

      {/* Delete pin confirmation dialog */}
      <DeletePinDialog
        open={deletePinDialogOpen}
        onClose={() => {
          setDeletePinDialogOpen(false);
          setPinToDelete(null);
        }}
        onConfirm={confirmDeletePin}
        pinName={pinToDelete?.name || ""}
      />
    </Box>
  );
};
